import React, { useState, useContext, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Alert,
  AlertTitle,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Divider
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  AccountBalance as BankIcon,
  CreditCard as CreditCardIcon,
  AccountBalanceWallet as PayPalIcon,
  AttachFile as AttachFileIcon,
  DataObject as DataIcon
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import Dashboard from '../../Layouts/Dashboard';
import HeaderActions from '../../Components/HeaderActions';
import { HttpRequestContext } from '../../Blocks/HttpRequestProvider';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

// Define breadcrumbs for this page
const uploadTransactionsBreadcrumbs = [
  { href: '/', label: 'Accueil' },
  { href: '/accounting/dashboard', label: 'Comptabilité' },
  { label: 'Importer Transactions' }
];

const UploadTransactionsContent = () => {
  const { sendRequestWithToast } = useContext(HttpRequestContext);
  const [activeStep, setActiveStep] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingResults, setProcessingResults] = useState(null);
  const [selectedAccountType, setSelectedAccountType] = useState('');
  const [selectedAccount, setSelectedAccount] = useState('');
  const [previewData, setPreviewData] = useState(null);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);

  // Mock account data
  const accountTypes = [
    { value: 'bank', label: 'Compte Bancaire', icon: <BankIcon /> },
    { value: 'paypal', label: 'PayPal', icon: <PayPalIcon /> },
    { value: 'credit', label: 'Carte de Crédit', icon: <CreditCardIcon /> }
  ];

  const accounts = {
    bank: [
      { value: 'main_account', label: 'Compte Principal - BNP Paribas (****1234)' },
      { value: 'savings_account', label: 'Compte Épargne - BNP Paribas (****5678)' },
      { value: 'business_account', label: 'Compte Professionnel - ING (****9012)' }
    ],
    paypal: [
      { value: 'paypal_business', label: 'PayPal Business - business@example.com' },
      { value: 'paypal_personal', label: 'PayPal Personnel - personal@example.com' }
    ],
    credit: [
      { value: 'visa_card', label: 'Visa Business - ****3456' },
      { value: 'mastercard', label: 'Mastercard - ****7890' }
    ]
  };

  const steps = [
    'Sélectionner le type de compte',
    'Télécharger les fichiers',
    'Prévisualiser et valider',
    'Traitement terminé'
  ];

  // File upload handling
  const onDrop = useCallback((acceptedFiles) => {
    const newFiles = acceptedFiles.map(file => ({
      id: Date.now() + Math.random(),
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      status: 'uploaded',
      preview: null,
      transactions: null
    }));
    
    setUploadedFiles(prev => [...prev, ...newFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/plain': ['.txt'],
      'application/pdf': ['.pdf']
    },
    multiple: true
  });

  const removeFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (type) => {
    if (type.includes('csv') || type.includes('excel') || type.includes('spreadsheet')) {
      return <DataIcon color="success" />;
    }
    if (type.includes('pdf')) {
      return <AttachFileIcon color="error" />;
    }
    return <AttachFileIcon color="primary" />;
  };

  const processFiles = async () => {
    setIsProcessing(true);
    
    // Simulate file processing
    setTimeout(() => {
      const mockResults = {
        totalFiles: uploadedFiles.length,
        successfulFiles: uploadedFiles.length - 1,
        failedFiles: 1,
        totalTransactions: 156,
        newTransactions: 142,
        duplicateTransactions: 14,
        errors: [
          {
            file: uploadedFiles[0]?.name || 'file1.csv',
            error: 'Format de date non reconnu à la ligne 23'
          }
        ],
        summary: {
          totalAmount: 15678.90,
          incomeAmount: 23456.78,
          expenseAmount: -7777.88,
          dateRange: {
            from: '2024-01-01',
            to: '2024-01-31'
          }
        }
      };
      
      setProcessingResults(mockResults);
      setIsProcessing(false);
      setActiveStep(3);
    }, 3000);
  };

  const previewFile = (file) => {
    // Mock preview data
    const mockPreview = {
      fileName: file.name,
      totalRows: 45,
      columns: ['Date', 'Description', 'Montant', 'Solde'],
      sampleData: [
        { date: '2024-01-15', description: 'Virement SALAIRE', amount: 3500.00, balance: 12345.67 },
        { date: '2024-01-14', description: 'PRELEVEMENT EDF', amount: -156.78, balance: 8845.67 },
        { date: '2024-01-13', description: 'CB SUPERMARCHE', amount: -89.45, balance: 9002.45 },
        { date: '2024-01-12', description: 'VIREMENT RECU', amount: 1200.00, balance: 9091.90 },
        { date: '2024-01-11', description: 'CB RESTAURANT', amount: -45.80, balance: 7891.90 }
      ]
    };
    
    setPreviewData(mockPreview);
    setShowPreviewDialog(true);
  };

  const handleNext = () => {
    if (activeStep === 0 && selectedAccountType && selectedAccount) {
      setActiveStep(1);
    } else if (activeStep === 1 && uploadedFiles.length > 0) {
      setActiveStep(2);
    } else if (activeStep === 2) {
      processFiles();
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const canProceed = () => {
    switch (activeStep) {
      case 0:
        return selectedAccountType && selectedAccount;
      case 1:
        return uploadedFiles.length > 0;
      case 2:
        return true;
      default:
        return false;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <HeaderActions
        customActions={[
          <Button
            key="back-to-dashboard"
            variant="outlined"
            onClick={() => window.history.back()}
          >
            Retour au Tableau de Bord
          </Button>
        ]}
      />

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Importation de Transactions
        </Typography>
        <Typography variant="body2" color="textSecondary" paragraph>
          Importez vos relevés bancaires, extraits PayPal ou relevés de carte de crédit pour automatiser votre comptabilité.
        </Typography>

        <Stepper activeStep={activeStep} orientation="vertical">
          {/* Step 1: Account Selection */}
          <Step>
            <StepLabel>Sélectionner le type de compte</StepLabel>
            <StepContent>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Type de compte</InputLabel>
                    <Select
                      value={selectedAccountType}
                      onChange={(e) => {
                        setSelectedAccountType(e.target.value);
                        setSelectedAccount('');
                      }}
                      label="Type de compte"
                    >
                      {accountTypes.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {type.icon}
                            <Typography sx={{ ml: 1 }}>{type.label}</Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                {selectedAccountType && (
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Compte spécifique</InputLabel>
                      <Select
                        value={selectedAccount}
                        onChange={(e) => setSelectedAccount(e.target.value)}
                        label="Compte spécifique"
                      >
                        {accounts[selectedAccountType]?.map((account) => (
                          <MenuItem key={account.value} value={account.value}>
                            {account.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
              </Grid>
              
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={!canProceed()}
                >
                  Continuer
                </Button>
              </Box>
            </StepContent>
          </Step>

          {/* Step 2: File Upload */}
          <Step>
            <StepLabel>Télécharger les fichiers</StepLabel>
            <StepContent>
              <Box sx={{ mt: 2 }}>
                <Paper
                  {...getRootProps()}
                  sx={{
                    p: 4,
                    border: '2px dashed',
                    borderColor: isDragActive ? 'primary.main' : 'grey.300',
                    backgroundColor: isDragActive ? 'primary.light' : 'grey.50',
                    cursor: 'pointer',
                    textAlign: 'center',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <input {...getInputProps()} />
                  <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    {isDragActive ? 'Déposez vos fichiers ici' : 'Glissez-déposez vos fichiers ou cliquez pour sélectionner'}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Formats acceptés: CSV, XLS, XLSX, TXT, PDF
                  </Typography>
                </Paper>

                {uploadedFiles.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Fichiers téléchargés ({uploadedFiles.length})
                    </Typography>
                    <TableContainer component={Paper}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Fichier</TableCell>
                            <TableCell>Taille</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell align="center">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {uploadedFiles.map((file) => (
                            <TableRow key={file.id}>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  {getFileIcon(file.type)}
                                  <Typography sx={{ ml: 1 }}>{file.name}</Typography>
                                </Box>
                              </TableCell>
                              <TableCell>{formatFileSize(file.size)}</TableCell>
                              <TableCell>
                                <Chip label={file.type.split('/')[1]?.toUpperCase() || 'Unknown'} size="small" />
                              </TableCell>
                              <TableCell align="center">
                                <Tooltip title="Prévisualiser">
                                  <IconButton size="small" onClick={() => previewFile(file)}>
                                    <VisibilityIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Supprimer">
                                  <IconButton size="small" color="error" onClick={() => removeFile(file.id)}>
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </Box>

              <Box sx={{ mt: 2 }}>
                <Button onClick={handleBack} sx={{ mr: 1 }}>
                  Retour
                </Button>
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={!canProceed()}
                >
                  Continuer
                </Button>
              </Box>
            </StepContent>
          </Step>

          {/* Step 3: Preview and Validation */}
          <Step>
            <StepLabel>Prévisualiser et valider</StepLabel>
            <StepContent>
              <Alert severity="info" sx={{ mb: 2 }}>
                <AlertTitle>Vérification avant traitement</AlertTitle>
                Vérifiez les informations ci-dessous avant de lancer le traitement des transactions.
              </Alert>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Compte sélectionné
                      </Typography>
                      <Typography variant="body2">
                        Type: {accountTypes.find(t => t.value === selectedAccountType)?.label}
                      </Typography>
                      <Typography variant="body2">
                        Compte: {accounts[selectedAccountType]?.find(a => a.value === selectedAccount)?.label}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Fichiers à traiter
                      </Typography>
                      <Typography variant="body2">
                        Nombre de fichiers: {uploadedFiles.length}
                      </Typography>
                      <Typography variant="body2">
                        Taille totale: {formatFileSize(uploadedFiles.reduce((sum, f) => sum + f.size, 0))}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Box sx={{ mt: 2 }}>
                <Button onClick={handleBack} sx={{ mr: 1 }}>
                  Retour
                </Button>
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={isProcessing}
                >
                  {isProcessing ? 'Traitement en cours...' : 'Lancer le traitement'}
                </Button>
              </Box>

              {isProcessing && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="body2" gutterBottom>
                    Traitement des fichiers en cours...
                  </Typography>
                  <LinearProgress />
                </Box>
              )}
            </StepContent>
          </Step>

          {/* Step 4: Results */}
          <Step>
            <StepLabel>Traitement terminé</StepLabel>
            <StepContent>
              {processingResults && (
                <Box>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    <AlertTitle>Traitement terminé avec succès</AlertTitle>
                    {processingResults.newTransactions} nouvelles transactions ont été importées.
                  </Alert>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            Résumé du traitement
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2">Fichiers traités:</Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {processingResults.successfulFiles}/{processingResults.totalFiles}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2">Nouvelles transactions:</Typography>
                            <Typography variant="body2" fontWeight="bold" color="success.main">
                              {processingResults.newTransactions}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2">Doublons ignorés:</Typography>
                            <Typography variant="body2" fontWeight="bold" color="warning.main">
                              {processingResults.duplicateTransactions}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            Résumé financier
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2">Revenus:</Typography>
                            <Typography variant="body2" fontWeight="bold" color="success.main">
                              +{new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(processingResults.summary.incomeAmount)}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2">Dépenses:</Typography>
                            <Typography variant="body2" fontWeight="bold" color="error.main">
                              {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(processingResults.summary.expenseAmount)}
                            </Typography>
                          </Box>
                          <Divider sx={{ my: 1 }} />
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" fontWeight="bold">Total net:</Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(processingResults.summary.totalAmount)}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>

                  {processingResults.errors.length > 0 && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      <AlertTitle>Erreurs détectées</AlertTitle>
                      {processingResults.errors.map((error, index) => (
                        <Typography key={index} variant="body2">
                          • {error.file}: {error.error}
                        </Typography>
                      ))}
                    </Alert>
                  )}

                  <Box sx={{ mt: 3 }}>
                    <Button
                      variant="contained"
                      onClick={() => window.location.href = '/accounting/dashboard'}
                      sx={{ mr: 1 }}
                    >
                      Retour au Tableau de Bord
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => window.location.href = '/accounting/transactions'}
                    >
                      Voir les Transactions
                    </Button>
                  </Box>
                </Box>
              )}
            </StepContent>
          </Step>
        </Stepper>
      </Paper>

      {/* Preview Dialog */}
      <Dialog
        open={showPreviewDialog}
        onClose={() => setShowPreviewDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Aperçu du fichier: {previewData?.fileName}
        </DialogTitle>
        <DialogContent>
          {previewData && (
            <Box>
              <Typography variant="body2" gutterBottom>
                {previewData.totalRows} lignes détectées
              </Typography>
              <TableContainer component={Paper} sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {previewData.columns.map((col) => (
                        <TableCell key={col}>{col}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewData.sampleData.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell>{row.date}</TableCell>
                        <TableCell>{row.description}</TableCell>
                        <TableCell>
                          {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(row.amount)}
                        </TableCell>
                        <TableCell>
                          {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(row.balance)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPreviewDialog(false)}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Main component wrapped with Dashboard layout
const UploadTransactions = () => {
  return (
    <Dashboard 
      dashboardTitle="Importer Transactions"
      breadcrumbs={uploadTransactionsBreadcrumbs}
    >
      <UploadTransactionsContent />
    </Dashboard>
  );
};

export default UploadTransactions; 